import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ConfirmModal from "../components/ConfirmModal";
import AlertModal from "../components/AlertModal";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { HAMPER_DISCOUNT } from "../../src/config/discounts";

function Checkout() {
  const {
    selectedFlowers,
    selectedAccessories,
    selectedVips,
    hamperItems,
    hamperType,
    buildModeUsedRef,
    validHamper,
    extras,
    clearCart,
    deliveryNote,
    giftMessage,
    deliveryCity,
    wantsSameDay,
    sameDayFee,
    setDeliveryNote,
    setGiftMessage,
    deliveryAddress,
    hamperQuantity = 1,
    paymentMethod,
    setPaymentMethod,
  } = useCart();

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentStatus = params.get("payment");

  useEffect(() => {
    if (!user) {
      navigate("/signin", { replace: true, state: { from: "/checkout" } });
    }
  }, [user, navigate]);

  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoIsPercent, setPromoIsPercent] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    fetch(`${BASE_URL}/api/settings`)
      .then(res => res.json())
      .then(data => setSiteSettings(data));
  }, [BASE_URL]);


  // Price helpers
  const safePrice = (p) => {
      const num = parseFloat(p);
      return isNaN(num) ? 0 : num;
    };
  
    const hamperSubtotal = hamperItems && hamperItems.length > 0
      ? hamperItems.reduce(
          (sum, item) => sum + parseFloat(item?.price || 0),
          0
        )
      : 0;
  
    // ✅ Calculate extras total
      const extrasTotal =
        extras.flowers.reduce((sum, f) => sum + safePrice(f?.price) * (f.quantity || 1), 0) +
        extras.accessories.reduce((sum, a) => sum + safePrice(a?.price) * (a.quantity || 1), 0) +
        extras.vips.reduce((sum, v) => sum + safePrice(v?.price) * (v.quantity || 1), 0);
    
      // ✅ Base hamper calculations
      const hamperSubtotalTotal = hamperSubtotal * hamperQuantity;
      const hamperDiscountRate = HAMPER_DISCOUNT[hamperType] || 0;
      const hamperDiscount = hamperSubtotalTotal * hamperDiscountRate;
    
      // ✅ Subtotal before tax
      const hamperTotal = hamperSubtotalTotal - hamperDiscount;
      const subtotal = hamperTotal + extrasTotal;
    
      // ✅ Tax calculation (6.25%)
      const TAX_RATE = 0.0625;

      // compute actual discount in dollars
      const promoDiscountValue = promoDiscount > 0
        ? promoIsPercent
          ? Number((subtotal * (promoDiscount / 100)).toFixed(2))
          : Number(promoDiscount) // already a dollar value
        : 0;

      // now use promoDiscountValue in totals
      const discountedSubtotal = Math.max(0, subtotal - promoDiscountValue);
      const subtotalOverall = discountedSubtotal + sameDayFee;
      const taxAmount = Number((discountedSubtotal * TAX_RATE).toFixed(2));
      const totalAmount = Number((discountedSubtotal + taxAmount + sameDayFee).toFixed(2));

  // Redirect if no items
    useEffect(() => {
      // User came from payment cancel? Allow them to stay on checkout
      if (location.state?.from === "cancel") return;

      // Check if hamper exists (build mode completed)
      const hamperExists = buildModeUsedRef.current && hamperItems.length > 0;

      // Check if extras exist (normal shopping items)
      const extrasExist =
        selectedFlowers.length > 0 ||
        selectedAccessories.length > 0 ||
        selectedVips.length > 0;

      // If no hamper and no extras, send user home
      if (!hamperExists && !extrasExist && !paymentStatus) {
        navigate("/", { replace: true });
      }
    }, [
      buildModeUsedRef,
      hamperItems,
      selectedFlowers,
      selectedAccessories,
      selectedVips,
      location.state,
      paymentStatus,
      navigate
    ]);

  // Promo code application
  const handleApplyPromo = async () => {
    try {
      const res = await axios.post(
        `${BASE_URL}/api/promos/validate`,
        { code: promoCode },
        { withCredentials: true }
      );

      setPromoDiscount(res.data.discountAmount ?? 0);
      setPromoIsPercent(Boolean(res.data.isPercent));
      setPromoMessage(res.data.message || "Promo applied");
    } catch (err) {
      setPromoDiscount(0);
      setPromoIsPercent(false);
      setPromoMessage(err.response?.data?.message || "Invalid promo code");
    }
  };

  const handleCancelOrder = () => {
    clearCart();
    navigate("/cart", { state: { from: "cancel" }, replace: true });
  };

  const handlePayment = async () => {
    if (!paymentMethod) {
      alert("Please select a payment method before proceeding.");
      return;
    }

    setLoadingPayment(true);

    const orderDetails = {
      hamperType,
      hamperItems: Array.isArray(hamperItems) ? hamperItems : [],
      selectedFlowers: Array.isArray(extras.flowers) ? extras.flowers : [],
      selectedAccessories: Array.isArray(extras.accessories) ? extras.accessories : [],
      selectedVips: Array.isArray(extras.vips) ? extras.vips : [],
      deliveryCity,
      deliveryAddress,
      deliveryNote: deliveryNote || "",
      giftMessage: giftMessage || "",
      wantsSameDay: Boolean(wantsSameDay),
      sameDayFee: Number(sameDayFee) || 0,
      totalAmount: Number(totalAmount) || 0,
      taxAmount: Number(taxAmount) || 0,
      taxRate: TAX_RATE,
      hamperSubtotal: Number(hamperSubtotal) || 0,
      hamperQuantity: Number(hamperQuantity) || 1,
      hamperDiscount: Number(hamperDiscount) || 0,
      promoDiscount: Number(promoDiscount) || 0,
      promoDiscountValue: Number(promoDiscountValue) || 0,
      paymentMethod,
      status: "Pending Payment",
      promoCode: promoDiscount > 0
        ? { code: promoCode, discountAmount: promoDiscount, discountValue: promoDiscountValue, isPercent: promoIsPercent }
        : null,
    };

    try {
      const res = await fetch(`${BASE_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(orderDetails),
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Order creation failed:", errorData);
        setLoadingPayment(false);
        return;
      }

      const savedOrder = await res.json();

      // ✅ Save order temporarily for after redirect
      localStorage.setItem("lastOrder", JSON.stringify(savedOrder));

      // ✅ Redirect user to Stripe or PayPal
      if (paymentMethod === "stripe") {
        const stripeRes = await fetch(`${BASE_URL}/api/payments/stripe-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            totalAmount: savedOrder.totalAmount,
            orderId: savedOrder._id,          // internal Mongo ID
            orderNumber: savedOrder.orderNumber, // human-friendly number
          }),
        });

        const data = await stripeRes.json();
        if (data.url) {
          window.location.href = data.url; // go to Stripe Checkout
        }
      } else if (paymentMethod === "paypal") {
        const paypalRes = await fetch(`${BASE_URL}/api/payments/paypal-create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            totalAmount: savedOrder.totalAmount,
            orderId: savedOrder._id,          // internal Mongo ID
            orderNumber: savedOrder.orderNumber, // human-friendly number
          }),
        });

        const data = await paypalRes.json();
        if (data.approveUrl) {
          window.location.href = data.approveUrl; // go to PayPal Checkout
        }
      }

    } catch (err) {
      console.error("Order submission failed:", err);
    } finally {
      setLoadingPayment(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentParam = params.get("payment");

    if (!paymentParam) return;

    const savedOrder = JSON.parse(localStorage.getItem("lastOrder"));

    let method = "";
    let isSuccess = false;

    // Determine which gateway and status
    if (paymentParam === "stripe_success") {
      method = "stripe";
      isSuccess = true;
    } else if (paymentParam === "paypal_success") {
      method = "paypal";
      isSuccess = true;
    } else if (paymentParam === "stripe_failed") {
      method = "stripe";
    } else if (paymentParam === "paypal_failed") {
      method = "paypal";
    }

    // Update paymentMethod state for the modal message
    setPaymentMethod(method);

    if (isSuccess) {
      if (savedOrder) {
        setShowSuccessAlert(true);

        setTimeout(() => {
          setDeliveryNote("");
          setGiftMessage("");

          navigate("/order-confirmation", {
            state: {
              orderDetails: savedOrder,
              orderNumber: savedOrder.orderNumber,
              clearAfter: true,
            },
          });

          setTimeout(() => setShowSuccessAlert(false), 500);
          localStorage.removeItem("lastOrder");
        }, 3000);
      }
    } else if (paymentParam.endsWith("_failed")) {
      setShowErrorAlert(true);

      setTimeout(() => {
        setShowErrorAlert(false);
        navigate("/cart");
        localStorage.removeItem("lastOrder");
      }, 4000);
    }

    // ✅ Clean query params so popup doesn’t reappear on refresh
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
  }, [navigate, setDeliveryNote, setGiftMessage, setPaymentMethod]);


  const formatCutoffTime = time24 => {
    if (!time24) return "";
    const [h, m] = time24.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  return (
    <>
      <section className="bg-gray-100 py-10 text-center px-4 md:px-6">
        <h1 className="text-4xl font-bold mb-4">Checkout</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Review your order and complete your payment securely.
        </p>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12 relative">
        <div className="bg-white shadow p-6 sm:p-8 rounded-xl border border-gray-200 space-y-6">
          <div className="pt-4 mt-4">
            <div className="flex justify-between items-center text-lg font-bold">
              <span className="text-2xl font-bold">Total</span>
              <span className="text-2xl font-bold text-blue-700">
                ${totalAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span>Subtotal</span>
              <span>${!isNaN(subtotalOverall) ? subtotalOverall.toFixed(2) : "0.00"}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span>Tax</span>
              <span>+ ${!isNaN(taxAmount) ? taxAmount.toFixed(2) : "0.00"}</span>
            </div>
            <button
              onClick={() => setShowOrderDetails(!showOrderDetails)}
              className="text-sm text-blue-600 mt-1 underline"
            >
              {showOrderDetails ? "Hide Order Details" : "View Order Details"}
            </button>
          </div>

          {showOrderDetails && (
            <div className="mt-4 space-y-4 text-sm text-gray-700">
              {validHamper && (
                <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50 shadow-sm mb-4">
                  <h4 className="font-semibold text-lg text-gray-800 mb-3">
                    Hamper Package: {hamperType}
                  </h4>

                  {/* Flowers */}
                  {hamperItems?.flower && (
                    <>
                      <p className="text-sm font-medium text-gray-800 mb-1">Flowers</p>
                      <div className="flex justify-between text-gray-700 mb-1">
                        <span>{hamperItems.flower.name}</span>
                        <span>${parseFloat(hamperItems.flower.price).toFixed(2)}</span>
                      </div>
                      <hr></hr>
                    </>
                  )}

                  {/* Accessories */}
                  {hamperItems?.accessories?.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-gray-800 mt-2 mb-1">Accessories</p>
                      {hamperItems.accessories.map((acc, i) => (
                        <div key={`acc-${i}`} className="flex justify-between text-gray-700 mb-1">
                          <span>{acc.name}</span>
                          <span>${parseFloat(acc.price).toFixed(2)}</span>
                        </div>
                      ))}
                      <hr></hr>
                    </>
                  )}

                  {/* VIPs */}
                  {hamperItems?.vips?.length > 0 && (
                    <>
                      <p className="text-sm font-medium text-gray-800 mt-2 mb-1">VIPs</p>
                      {hamperItems.vips.map((vip, i) => (
                        <div key={`vip-${i}`} className="flex justify-between text-gray-700 mb-1">
                          <span>{vip.name}</span>
                          <span>${parseFloat(vip.price).toFixed(2)}</span>
                        </div>
                      ))}
                      <hr></hr>
                    </>
                  )}

                  <div className="flex justify-between mt-2 text-gray-600 font-medium">
                    <span>Total Number of Hamper Package is ({hamperQuantity})</span>
                  </div>

                  {/* Subtotal */}
                  <div className="flex justify-between mt-3 text-gray-600 font-medium">
                    <span>Hamper Subtotal × {hamperQuantity}</span>
                    <span>${(hamperSubtotal * hamperQuantity).toFixed(2)}</span>
                  </div>

                  {/* Discount */}
                  {hamperDiscount > 0 && (
                    <div className="flex justify-between text-green-600 font-medium mt-1">
                      <span>Hamper Discount ({(HAMPER_DISCOUNT[hamperType] * 100).toFixed(0)}%)</span>
                      <span>- ${hamperDiscount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Extra Flowers */}
              {extras.flowers.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">
                    {validHamper ? "Extra Flowers" : "Flowers"}
                  </h4>
                  {extras.flowers.map((f, idx) => (
                    <div key={idx} className="flex justify-between text-gray-700 mb-1">
                      <span>{f.name} × {f.quantity || 1}</span>
                      <span>${(parseFloat(f.price) * (f.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Extra Accessories */}
              {extras.accessories.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">
                    {validHamper ? "Extra Accessories" : "Accessories"}
                  </h4>
                  {extras.accessories.map((a, idx) => (
                    <div key={idx} className="flex justify-between text-gray-700 mb-1">
                      <span>{a.name} × {a.quantity || 1}</span>
                      <span>${(parseFloat(a.price) * (a.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Extra VIPs */}
              {extras.vips.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-base text-gray-800 mb-1">
                    {validHamper ? "Extra VIPs" : "VIPs"}
                  </h4>
                  {extras.vips.map((v, idx) => (
                    <div key={idx} className="flex justify-between text-gray-700 mb-1">
                      <span>{v.name} × {v.quantity || 1}</span>
                      <span>${(parseFloat(v.price) * (v.quantity || 1)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              {sameDayFee > 0 && (
                <div className="flex justify-between mt-2 text-red-500">
                  <span>
                    Same-day Delivery (ordered before {formatCutoffTime(siteSettings?.cutoffTime)})
                  </span>
                  <span>${sameDayFee.toFixed(2)}</span>
                </div>
              )}

              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>
                    Promo Discount{promoIsPercent ? ` (${promoDiscount}% Off)` : ""}
                  </span>
                  <span>- ${promoDiscountValue.toFixed(2)}</span>
                </div>
              )}

              {/* Delivery Address, Optional Delivery Note & Gift Message */}
              {deliveryAddress && (
                <div className="mt-4">
                  <p className="font-medium text-gray-700">Delivery Address:</p>
                  <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{deliveryAddress}</p>
                </div>
              )}

              {deliveryNote && (
                <div className="mt-4">
                  <p className="font-medium text-gray-700">Delivery Instruction:</p>
                  <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{deliveryNote}</p>
                </div>
              )}

              {giftMessage && (
                <div className="mt-4">
                  <p className="font-medium text-gray-700">Special Gift Message:</p>
                  <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{giftMessage}</p>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="text-sm block mb-1 font-semibold">Promo Code</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="w-full sm:flex-1 border rounded px-4 py-2"
                placeholder="Enter code..."
              />
              <button
                onClick={handleApplyPromo}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded"
              >
                Apply
              </button>
            </div>
            {promoMessage && (
              <p className={`mt-1 text-sm ${promoDiscount ? "text-green-600" : "text-red-500"}`}>
                {promoMessage}
              </p>
            )}
          </div>

          <div className="pt-10">
            <h2 className="text-lg font-semibold mb-4">Choose Payment Method</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* STRIPE (ACTIVE) */}
              <button
                onClick={() => setPaymentMethod("stripe")}
                className={`border p-4 rounded-lg hover:shadow-md flex items-center justify-center transition ${
                  paymentMethod === "stripe" ? "ring-2 ring-blue-500" : ""
                }`}
              >
                <img
                  src="/images/logos/stripe-logo.svg"
                  alt="Stripe"
                  className="h-10 object-contain"
                />
              </button>

              {/* PAYPAL (DISABLED) */}
              <div
                className="border p-4 rounded-lg flex flex-col items-center justify-center
                          bg-gray-100 opacity-60 cursor-not-allowed relative"
              >
                <img
                  src="/images/logos/paypal-logo.svg"
                  alt="PayPal"
                  className="h-10 object-contain mb-2"
                />

                <span className="text-xs text-gray-600 font-medium">
                  PayPal payments coming soon
                </span>

                {/* Optional badge */}
                <span className="absolute top-2 right-2 text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                  Coming soon
                </span>
              </div>
            </div>
          </div>

          <div className="pt-6 flex flex-col sm:flex-row justify-between gap-4">
            <button
              onClick={handlePayment}
              disabled={!paymentMethod || loadingPayment}
              className={`w-full sm:w-auto min-w-[250px] px-6 py-3 flex items-center justify-center gap-2 ${
                loadingPayment
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              } text-white font-semibold text-sm rounded-full transition-all duration-200`}
            >
              {loadingPayment ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  Pay ${totalAmount.toFixed(2)} Now with{" "}
                  {paymentMethod === "stripe" ? "Stripe" : "PayPal"}
                </>
              )}
            </button>
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full sm:w-auto px-6 py-3 bg-red-500 hover:bg-red-600 text-white text-sm rounded-full"
            >
              Cancel Order
            </button>
          </div>
        </div>

        <ConfirmModal
          isOpen={showCancelModal}
          title="Cancel Order"
          message="Are you sure you want to cancel this order? All selections will be cleared."
          onCancel={() => setShowCancelModal(false)}
          onConfirm={handleCancelOrder}
          onClose={() => setShowCancelModal(false)}
          confirmLabel="Yes, Cancel Order"
        />

        <AlertModal
          isOpen={showSuccessAlert}
          onClose={() => setShowSuccessAlert(false)}
          type="success"
          message={`Payment successful via ${paymentMethod.toUpperCase()}. Redirecting to receipt...`}
        />

        <AlertModal
          isOpen={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
          type="error"
          message={`Payment failed or was cancelled via ${paymentMethod?.toUpperCase() || "UNKNOWN"}. Redirecting to your cart...`}
        />
      </div>

      <Footer />
    </>
  );
}

export default Checkout;
